import { useState } from 'react';

import { useCartMutations } from '~/features/cart/cart-actions';
import { useProductData } from '~/features/products/products-actions';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import {
  analyticsBeacon,
  DeprecatedEventType,
  EventType,
} from '~/utils/analytics';
import { PAYMENT_TYPE } from '~/components/plans/bundles-page/bundles';
import { captureException } from '~/utils/exception-tracking';
import { useSession } from '~/features/session/session-actions';

export const useAddBundleToCart = ({ lawnUuid, disableNavigation = false }) => {
  const { showFlashMessage } = useFlashMessage();

  const { setActiveCart } = useCartMutations();
  const { user } = useSession();
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { products } = useProductData();

  const addBundleToCart = async (bundle, isFlexPay, isRecommended) => {
    setIsAddingToCart(true);

    let newCart;
    try {
      newCart = await setActiveCart(bundle?.selectedCartUuid);

      analyticsBeacon.emit(EventType.PLAN_TIER_SELECTED, {
        userUuid: user?.uuid,
        cartUuid: newCart.uuid,
        lawnUuid,
        planSku: bundle.lawnPlanBundleItem.skuId,
        planCode: bundle.uuid,
        planTier: bundle.title,
        planPrice: bundle.fullSeasonPrice,
        planCartItems: newCart.contents.filter(
          (cartItem) => cartItem.bundleUuid === bundle.uuid
        ),
        flexPayFirstBoxPrice: bundle?.carts[0]?.subtotal,
        pouchCount: bundle.lawnPlanPouchCount,
        cartContents: newCart.contents,
        products,
        paymentType: isFlexPay
          ? PAYMENT_TYPE.FLEX_PAY
          : PAYMENT_TYPE.PAY_IN_FULL,
        numberOfShipments: bundle.carts.length,
        isRecommended,
      });

      analyticsBeacon.emit(DeprecatedEventType.ADD_TO_CART, {
        cartItem: {
          sku: {
            skuId: bundle.uuid,
            unitPrice: bundle.unitPrice,
          },
          quantity: 1,
        },
        productDetails: {
          category: {
            sundayStoreProductCategory: {
              name: 'Smart Lawn Plan',
            },
          },
        },
      });

      showFlashMessage('Custom lawn plan added to your cart', 'success', 1000);
    } catch (err) {
      captureException(new Error('Error when adding a lawn bundle to cart'), {
        extras: {
          error: JSON.stringify(err),
          planSkus: JSON.stringify(bundle?.purchaseSkus),
        },
      });

      showFlashMessage(
        'Oops, there was a problem adding the plan to your cart',
        'error'
      );
      setIsAddingToCart(false);
    }

    setIsAddingToCart(false);

    return newCart;
  };

  return {
    isAddingToCart,
    addBundleToCart,
  };
};
